// Viento: estacionario y turbulento con semilla. Ver docs/theory/environment.md.
import { type Vec3, vec3 } from '../math/vec3';
import { mulberry32 } from '../random/prng';

export interface WindModel {
  /** Velocidad del aire (m/s, ENU) en el instante `t` (s). */
  at(t: number): Vec3;
}

export interface WindOptions {
  /** Velocidad media (m/s). */
  readonly speed: number;
  /** Dirección meteorológica: acimut desde donde sopla, horario desde el norte (rad). */
  readonly direction: number;
  /** Intensidad de turbulencia I: σ = I · speed. 0 = sin turbulencia. */
  readonly turbulenceIntensity?: number;
  /** Semilla de la turbulencia. */
  readonly seed?: number;
}

const COMPONENTS = 24;
const TAU_MIN = 1;
const TAU_MAX = 600;

interface Sinusoid {
  readonly omega: number;
  readonly phase: number;
  readonly amplitude: number;
}

function spectrum(rng: () => number, sigma: number): Sinusoid[] {
  const raw: Sinusoid[] = [];
  const fMin = 1 / TAU_MAX;
  const fMax = 1 / TAU_MIN;
  for (let k = 0; k < COMPONENTS; k++) {
    const f = fMin * Math.pow(fMax / fMin, (k + rng()) / COMPONENTS);
    raw.push({ omega: 2 * Math.PI * f, phase: 2 * Math.PI * rng(), amplitude: Math.pow(f, -5 / 6) });
  }
  // Normaliza para que la varianza Σ A²/2 sea σ².
  const power = raw.reduce((s, c) => s + (c.amplitude * c.amplitude) / 2, 0);
  const norm = power === 0 ? 0 : sigma / Math.sqrt(power);
  return raw.map((c) => ({ ...c, amplitude: c.amplitude * norm }));
}

const evaluate = (series: readonly Sinusoid[], t: number): number =>
  series.reduce((s, c) => s + c.amplitude * Math.sin(c.omega * t + c.phase), 0);

export function createWind(options: WindOptions): WindModel {
  const { speed, direction } = options;
  // Dirección hacia donde se mueve el aire y su perpendicular horizontal.
  const along = vec3(-Math.sin(direction), -Math.cos(direction), 0);
  const across = vec3(-along.y, along.x, 0);
  const sigma = (options.turbulenceIntensity ?? 0) * speed;

  if (sigma <= 0) {
    const steady = vec3(along.x * speed, along.y * speed, 0);
    return { at: () => steady };
  }

  const rng = mulberry32(options.seed ?? 0);
  const longitudinal = spectrum(rng, sigma);
  const transversal = spectrum(rng, sigma);
  return {
    at(t: number): Vec3 {
      const u = speed + evaluate(longitudinal, t);
      const v = evaluate(transversal, t);
      return vec3(along.x * u + across.x * v, along.y * u + across.y * v, 0);
    },
  };
}
